import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email });
      toast.success(data.message || "Thank you for joining us.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-eucalyptus-deep text-warmwhite overflow-hidden">
      <div className="grain absolute inset-0 opacity-30" />
      <div className="relative mx-auto max-w-[88rem] px-6 lg:px-10 py-20 lg:py-28">
        <div className="max-w-3xl">
          <p className="text-[11px] tracking-overline uppercase text-sandstone/80 mb-6">Acknowledgement of Country</p>
          <p className="font-heading text-2xl md:text-3xl font-light leading-snug tracking-tight text-warmwhite/95">
            Ranger Base acknowledges the Traditional Owners, Custodians and Indigenous
            peoples of the lands and waters where we work — in Australia and around the
            world. We pay our respects to Elders and knowledge holders past and present,
            who have cared for Country for tens of thousands of years.
          </p>
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-[1.4fr_1fr_1.2fr]">
          <div>
            <div className="flex items-center gap-3">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-warmwhite/10">
                <img src="/rangerbase-logo.png" alt="Ranger Base" className="h-10 w-10 object-contain" />
              </span>
              <div>
                <div className="font-heading font-extrabold tracking-tight text-lg">RANGER BASE</div>
                <div className="text-[10px] tracking-overline uppercase text-sandstone/70">Guardians of a Changing Country</div>
              </div>
            </div>
            <p className="mt-6 text-warmwhite/70 max-w-sm leading-relaxed">
              Building Indigenous stewardship institutions that care for Country, strengthen
              culture and create opportunity for future generations.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {["Instagram", "LinkedIn", "YouTube"].map((s) => (
                <a
                  key={s}
                  href="#"
                  data-testid={`social-${s.toLowerCase()}`}
                  className="text-xs tracking-wide uppercase px-4 py-2 rounded-full border border-warmwhite/20 hover:bg-warmwhite/10 transition-colors"
                >
                  {s}
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] tracking-overline uppercase text-sandstone/70 mb-5">Explore</p>
            <ul className="space-y-3">
              {[{ label: "Home", path: "/" }, ...NAV_LINKS, { label: "Contact", path: "/contact" }].map((l) => (
                <li key={l.path}>
                  <Link to={l.path} data-testid={`footer-link-${l.label.toLowerCase()}`} className="text-warmwhite/75 hover:text-warmwhite transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-[11px] tracking-overline uppercase text-sandstone/70 mb-5">Newsletter</p>
            <p className="text-warmwhite/70 mb-4 leading-relaxed">
              Stories from Country, new resources and program updates. Supporting Indigenous ranger programs around the world.
            </p>
            <form onSubmit={subscribe} className="flex flex-col gap-3" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="newsletter-email-input"
                className="w-full rounded-full bg-warmwhite/10 border border-warmwhite/20 px-5 py-3 text-warmwhite placeholder:text-warmwhite/40 focus:outline-none focus:border-ochre transition-colors"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-submit"
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-ochre text-white px-6 py-3 font-semibold hover:bg-ochre/90 transition-colors disabled:opacity-60"
              >
                {busy ? "Joining…" : "Join us"} <ArrowUpRight size={16} />
              </button>
            </form>
            <p className="mt-6 text-warmwhite/60 text-sm">hello@rangerbase.com</p>
          </div>
        </div>

        <div className="mt-16 flex flex-col sm:flex-row items-center gap-6 rounded-3xl bg-warmwhite p-6 sm:p-8" data-testid="world-ranger-day">
          <img src="/stand-with-rangers.jpg" alt="We Stand With The World's Rangers — World Ranger Day, 31 July" className="w-full sm:w-56 rounded-2xl object-contain shrink-0" />
          <div>
            <p className="text-[11px] tracking-overline uppercase text-ochre mb-2">Proudly supporting</p>
            <p className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-eucalyptus-deep leading-tight">We stand with the world's rangers.</p>
            <p className="mt-2 text-charcoal/70 leading-relaxed max-w-md">Ranger Base celebrates World Ranger Day on 31 July, honouring the rangers who protect Country, culture and nature around the world.</p>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-warmwhite/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-warmwhite/55">
          <span>© {new Date().getFullYear()} Ranger Base. Walking together for Country.</span>
          <span>Global service delivery · Working worldwide</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
